/**
 * This helper function is intended for use with `FrontDoorsPageContainer`
 * It accepts an instance of `track`, an `appLink` and the optional parameter `additionalTrackProperties`
 * It returns default event handlers, each containing a `track` event with the relevant payload
 * These default event handlers can be overriden as required for custom use cases
 *
 * Example Usage:
 *
 * import { getDefaultEventHandlers } from '@strava/front-doors-page-container-ui';
 *
 * const additionalTrackProperties = { myAdditionalTrackProperty: 'my-additional-track-property-value' };
 * const myCustomOnScreenEnterCallback = () => { ... };
 *
 * const { appLink } = useStravaBranch();
 *
 * <FrontDoorsPageContainer
 *  {...getDefaultEventHandlers(track, appLink, additionalTrackProperties)}
 *  onScreenEnter={myCustomOnScreenEnterCallback}
 * >
 *  {children}
 * </FrontDoorsPageContainer>
 *
 * @param {Function} track Track function (Required)
 * @param {String} appLink Branch link
 * @param {object} additionalTrackProperties object of properties to be added to `properties` within `track` events (Optional)
 * @returns {object} Object containing default event handlers
 *
 */

const getDefaultEventHandlers = (track, appLink, additionalTrackProperties) => {
  // Common track event used in `onDesktopHeaderLogoClick` and `onMobileHeaderLogoClick`
  const onHeaderLogoClickTrackEvent = () => {
    track({
      element: 'strava_logo',
      action: 'click',
      ...(additionalTrackProperties && {
        properties: additionalTrackProperties
      })
    });
  };

  return {
    // Called on screen enter
    onScreenEnter: () => {
      track({
        action: 'screen_enter',
        ...(additionalTrackProperties && {
          properties: additionalTrackProperties
        })
      });
    },

    // Called when athlete clicks on Strava logo on desktop header
    onDesktopHeaderLogoClick: () => {
      onHeaderLogoClickTrackEvent();
    },

    // Called when athlete clicks on 'Login' button on desktop header
    onDesktopHeaderLoginBtnClick: () => {
      track({
        element: 'login_header',
        action: 'click',
        ...(additionalTrackProperties && {
          properties: additionalTrackProperties
        })
      });
    },

    // Called when athlete clicks on the 'Join for Free' button on desktop header
    onDesktopHeaderSignupBtnClick: () => {
      track({
        element: 'start_signup',
        action: 'click',
        properties: {
          signup_copy: 'join_header',
          ...additionalTrackProperties
        }
      });
    },

    // Called when athlete clicks on Strava logo on mobile header
    onMobileHeaderLogoClick: () => {
      onHeaderLogoClickTrackEvent();
    },

    // Called when athlete clicks on the 'Get the App' button on mobile header
    onMobileHeaderSignupBtnClick: () => {
      track({
        element: 'get_app',
        action: 'click',
        properties: {
          cta_copy: 'get_the_app_header',
          url: appLink,
          ...additionalTrackProperties
        }
      });
    },

    // Called when athlete opens the menu on mobile header
    onMobileHeaderMenuOpen: () => {
      track({
        element: 'open_menu',
        action: 'click',
        ...(additionalTrackProperties && {
          properties: additionalTrackProperties
        })
      });
    },

    // Called when athlete closes the menu on mobile header
    onMobileHeaderMenuClose: () => {
      track({
        element: 'close_menu',
        action: 'click',
        ...(additionalTrackProperties && {
          properties: additionalTrackProperties
        })
      });
    },

    // Called when athlete clicks on the 'Login' button in mobile menu
    onMobileHeaderMenuLoginBtnClick: () => {
      track({
        element: 'drawer_login_btn',
        action: 'click',
        ...(additionalTrackProperties && {
          properties: additionalTrackProperties
        })
      });
    },

    // Called when athlete clicks on the 'Get the App' button in mobile menu
    onMobileHeaderMenuSignupBtnClick: () => {
      track({
        element: 'get_app',
        action: 'click',
        properties: {
          cta_copy: 'get_the_app_drawer',
          url: appLink,
          ...additionalTrackProperties
        }
      });
    },

    // Called when athlete clicks on app store badges in footer
    onFooterAppStoreBadgeClick: (id) => {
      track({
        element: 'get_app',
        action: 'click',
        properties: {
          url: appLink,
          cta_copy: `${id}_app_store`,
          ...additionalTrackProperties
        }
      });
    }
  };
};

export default getDefaultEventHandlers;
